import { useState, useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Power, Tv, Radio, ExternalLink } from 'lucide-react';

function getYoutubeId(url) {
  const m = (url || '').match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m?.[1] || null;
}

/**
 * VintageTV — a 60s wooden TV set that plays YouTube videos in sequence.
 * Props:
 *  - videos: [{ id, url, title, artistName, platform }]
 *  - onSelectVideo: optional callback when user opens fullscreen
 */
export default function VintageTV({ videos, onSelectVideo }) {
  const ytVideos = useMemo(
    () => (videos || []).filter(v => getYoutubeId(v?.url)),
    [videos]
  );
  const [channel, setChannel] = useState(0);
  const [powered, setPowered] = useState(true);
  // Start MUTED so the browser allows autoplay. The user can unmute via the
  // VOLUME knob — once unmuted the next video keeps the chosen volume.
  const [volume, setVolume] = useState(0);
  const iframeRef = useRef(null);

  // Reset channel if videos change
  useEffect(() => { if (channel >= ytVideos.length) setChannel(0); }, [ytVideos, channel]);

  const current = ytVideos[channel];
  const ytId = current ? getYoutubeId(current.url) : null;

  const next = () => setChannel(c => (c + 1) % Math.max(ytVideos.length, 1));
  const prev = () => setChannel(c => (c - 1 + ytVideos.length) % Math.max(ytVideos.length, 1));

  // Listen for YouTube iframe state changes (ended → auto-advance to next channel)
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== 'https://www.youtube.com' && e.origin !== 'https://www.youtube-nocookie.com') return;
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        // event: 'onStateChange', info: 0 = ENDED, 1 = PLAYING
        if (data && data.event === 'onStateChange') {
          if (data.info === 0) {
            setChannel(c => (c + 1) % Math.max(ytVideos.length, 1));
          } else if (data.info === 1) {
            // Video started playing → tell the global audio to pause
            window.dispatchEvent(new CustomEvent('kazajah:video-playing'));
          }
        }
      } catch { /* not a YT message */ }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [ytVideos.length]);

  // When the global audio player starts playing, pause our YouTube iframe.
  useEffect(() => {
    const onAudioPlay = () => {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
          'https://www.youtube.com'
        );
      } catch { /* ignore */ }
    };
    window.addEventListener('kazajah:audio-playing', onAudioPlay);
    return () => window.removeEventListener('kazajah:audio-playing', onAudioPlay);
  }, []);

  // Send the YouTube iframe API "listening" handshake whenever the video changes
  // so we receive onStateChange events.
  useEffect(() => {
    if (!iframeRef.current || !ytId) return;
    const iframe = iframeRef.current;
    const handshake = () => {
      try {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ event: 'listening', id: ytId, channel: 'widget' }),
          'https://www.youtube.com'
        );
        iframe.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'addEventListener', args: ['onStateChange'] }),
          'https://www.youtube.com'
        );
      } catch { /* iframe not ready yet */ }
    };
    iframe.addEventListener('load', handshake);
    // Also try right away in case load already fired
    const t = setTimeout(handshake, 800);
    return () => { iframe.removeEventListener('load', handshake); clearTimeout(t); };
  }, [ytId]);

  if (ytYTotalEmpty(ytVideos)) {
    return (
      <div className="text-center py-12 text-zinc-500" data-testid="vintage-tv-empty">
        <Tv size={32} className="mx-auto mb-2 opacity-50" />
        <p className="text-sm">Aucune video YouTube disponible.</p>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-3xl mx-auto" data-testid="vintage-tv">
      {/* Antennas */}
      <div className="relative h-16 flex items-end justify-center pointer-events-none">
        <div
          className="absolute left-1/2 -translate-x-1/2 bottom-0 w-1 h-14 bg-gradient-to-b from-zinc-300 to-zinc-600"
          style={{ transform: 'translateX(-90%) rotate(-22deg)', transformOrigin: 'bottom center' }}
        />
        <div
          className="absolute left-1/2 -translate-x-1/2 bottom-0 w-1 h-14 bg-gradient-to-b from-zinc-300 to-zinc-600"
          style={{ transform: 'translateX(60%) rotate(22deg)', transformOrigin: 'bottom center' }}
        />
        <div className="absolute left-1/2 bottom-0 w-3 h-3 -translate-x-1/2 rounded-full bg-zinc-700 shadow-md" />
      </div>

      {/* Wooden TV body */}
      <div
        className="relative rounded-[28px] p-5 sm:p-7 shadow-2xl"
        style={{
          background:
            'linear-gradient(135deg, #6b3e1a 0%, #8b552a 25%, #a0683a 50%, #6b3e1a 75%, #4a2810 100%)',
          boxShadow:
            '0 30px 60px rgba(0,0,0,0.55), inset 0 4px 12px rgba(255,225,180,0.12), inset 0 -8px 16px rgba(0,0,0,0.55)',
        }}
      >
        {/* Wood grain texture */}
        <div
          className="absolute inset-0 rounded-[28px] pointer-events-none opacity-30 mix-blend-overlay"
          style={{
            backgroundImage:
              'repeating-linear-gradient(90deg, rgba(60,30,10,0.5) 0, transparent 2px, transparent 8px), repeating-linear-gradient(45deg, rgba(120,80,40,0.2) 0, transparent 1px, transparent 5px)',
          }}
        />

        <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 items-center">
          {/* CRT Screen */}
          <div className="relative">
            {/* Screen frame (gold/brass) */}
            <div
              className="relative p-2 rounded-[24px]"
              style={{
                background: 'linear-gradient(135deg, #2a1a0a, #4a2c10, #2a1a0a)',
                boxShadow: 'inset 0 0 0 2px rgba(255,200,100,0.25), 0 4px 12px rgba(0,0,0,0.6)',
              }}
            >
              {/* Curved CRT bezel */}
              <div
                className="relative aspect-video rounded-[20px] overflow-hidden bg-black"
                style={{
                  boxShadow:
                    'inset 0 0 22px rgba(0,0,0,0.85), inset 0 0 4px rgba(120,90,40,0.55)',
                }}
              >
                {/* Iframe */}
                {powered && ytId ? (
                  <iframe
                    ref={iframeRef}
                    key={ytId}
                    src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=${volume === 0 ? 1 : 0}&controls=1&rel=0&enablejsapi=1&playsinline=1`}
                    title={current?.title || 'KAZAJAH TV'}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500">
                    <Power size={28} className="opacity-40 mb-2" />
                    <span className="text-[10px] tracking-widest uppercase opacity-50">Eteint</span>
                  </div>
                )}

                {/* Scanline overlay */}
                <div
                  className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-overlay"
                  style={{
                    background:
                      'repeating-linear-gradient(0deg, rgba(0,0,0,0.95) 0, transparent 2px, transparent 3px)',
                  }}
                />
                {/* Vignette */}
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)',
                  }}
                />
                {/* Glass shine */}
                <div
                  className="pointer-events-none absolute -inset-1 opacity-25"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, transparent 35%, transparent 65%, rgba(255,255,255,0.15) 100%)',
                  }}
                />
              </div>
            </div>

            {/* "KAZAJAH TV" gold label below screen */}
            <div className="mt-3 flex items-center justify-between gap-3 text-[10px]">
              <span
                className="font-black tracking-[0.35em] px-3 py-1 rounded-md"
                style={{
                  background: 'linear-gradient(135deg, #d4a149, #f7d572, #b88227)',
                  color: '#3a200a',
                  boxShadow: 'inset 0 0 4px rgba(0,0,0,0.3)',
                }}
              >
                KAZAJAH TV
              </span>
              <span className="text-amber-200/70 font-mono uppercase tracking-widest text-[9px]">
                CH {String(channel + 1).padStart(2, '0')} / {String(ytVideos.length).padStart(2, '0')}
              </span>
            </div>
          </div>

          {/* Side panel: knobs + buttons */}
          <div className="flex md:flex-col gap-4 items-center md:w-28 justify-around">
            {/* Channel selector */}
            <div className="text-center">
              <p className="text-[8px] tracking-widest text-amber-200/70 uppercase mb-1.5">Channel</p>
              <div className="flex items-center gap-1">
                <button onClick={prev}
                  data-testid="vintage-tv-prev"
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-transform active:scale-95"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #f1d68b, #b88227 65%, #6b4612)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.4)',
                  }}>
                  <ChevronLeft size={14} className="text-amber-900" />
                </button>
                <div className="w-12 h-12 rounded-full relative flex items-center justify-center"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #d4a149, #8b5a1f 65%, #4a2810)',
                    boxShadow: '0 3px 6px rgba(0,0,0,0.6), inset 0 2px 4px rgba(255,255,255,0.3)',
                  }}>
                  <span className="text-amber-100 text-base font-black">{channel + 1}</span>
                </div>
                <button onClick={next}
                  data-testid="vintage-tv-next"
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-transform active:scale-95"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #f1d68b, #b88227 65%, #6b4612)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.4)',
                  }}>
                  <ChevronRight size={14} className="text-amber-900" />
                </button>
              </div>
            </div>

            {/* Volume knob */}
            <div className="text-center">
              <p className="text-[8px] tracking-widest text-amber-200/70 uppercase mb-1.5">Volume</p>
              <div
                className="relative w-12 h-12 mx-auto rounded-full cursor-pointer"
                onClick={() => setVolume(v => v === 0 ? 60 : 0)}
                title={volume === 0 ? 'Activer son' : 'Couper son'}
                style={{
                  background: 'radial-gradient(circle at 30% 30%, #d4a149, #8b5a1f 65%, #4a2810)',
                  boxShadow: '0 3px 6px rgba(0,0,0,0.6), inset 0 2px 4px rgba(255,255,255,0.3)',
                }}
              >
                <div
                  className="absolute top-1 left-1/2 w-1 h-3 -translate-x-1/2 rounded-full bg-amber-100 origin-bottom"
                  style={{ transform: `translateX(-50%) rotate(${(volume - 50) * 1.6}deg)` }}
                />
              </div>
              <p className="text-[9px] text-amber-200/60 mt-1 font-mono">
                {volume === 0 ? 'MUTE' : `${volume}%`}
              </p>
            </div>

            {/* On/Off + ON AIR light */}
            <div className="text-center flex flex-col items-center">
              <button onClick={() => setPowered(p => !p)}
                data-testid="vintage-tv-power"
                className="w-9 h-9 rounded-full flex items-center justify-center transition-transform active:scale-95"
                style={{
                  background: 'radial-gradient(circle at 30% 30%, #404040, #1a1a1a)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.7), inset 0 1px 2px rgba(255,255,255,0.15)',
                }}>
                <Power size={14} className={powered ? 'text-red-400' : 'text-zinc-600'} />
              </button>
              <div
                className={`mt-2 w-2.5 h-2.5 rounded-full transition-all ${
                  powered ? 'bg-red-500 shadow-[0_0_8px_3px_rgba(239,68,68,0.6)]' : 'bg-zinc-800'
                }`}
              />
              <p className="text-[8px] tracking-widest mt-1 font-bold"
                style={{ color: powered ? '#fb7185' : '#52525b' }}>
                ON AIR
              </p>
            </div>
          </div>
        </div>

        {/* Speaker grille bottom */}
        <div className="mt-5 grid grid-cols-2 gap-3 items-center">
          <div className="h-8 rounded-md"
            style={{
              background:
                'repeating-linear-gradient(90deg, rgba(0,0,0,0.4) 0, rgba(0,0,0,0.4) 2px, rgba(80,50,20,0.3) 2px, rgba(80,50,20,0.3) 4px)',
              boxShadow: 'inset 0 0 8px rgba(0,0,0,0.6)',
            }}
          />
          <div className="flex items-center gap-2">
            <Radio size={12} className="text-amber-200/60" />
            <span className="text-[9px] uppercase tracking-widest text-amber-200/70 font-mono">
              VINTAGE • 1965
            </span>
          </div>
        </div>
      </div>

      {/* Now-playing caption */}
      {current && (
        <motion.div
          key={current.id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 text-center"
          data-testid="vintage-tv-caption"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold mb-1">
            En direct • {current.platform}
          </p>
          <h3 className="text-white font-bold text-base sm:text-lg">{current.title}</h3>
          <p className="text-zinc-400 text-xs">{current.artistName}</p>
          <div className="mt-2 flex items-center justify-center gap-2">
            <button
              onClick={() => onSelectVideo && onSelectVideo(current)}
              data-testid="vintage-tv-fullscreen"
              className="text-[11px] px-3 py-1 bg-yellow-500/15 text-yellow-300 border border-yellow-500/30 rounded-full hover:bg-yellow-500/25 transition-colors inline-flex items-center gap-1"
            >
              <ExternalLink size={11} /> Plein ecran
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function ytYTotalEmpty(arr) {
  return !Array.isArray(arr) || arr.length === 0;
}
